import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { GridCellEditStartParams, GridCellParams, GridValidRowModel, MuiEvent } from '@mui/x-data-grid-premium';
import { useAuthStore } from '../stores/auth.store';
import { canEditNotes, markNotesStale, saveNotes } from '../components/entity-notes/notes';
import { ENTITY_MODAL_NAMES, NIL_UUID } from '../pages/tracking/tracking-page.format';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Double-click editing of an entity grouping's Notes column, for the report and its drilldowns. */
export function useNotesEditing(groupBy: string) {
  const role = useAuthStore((st) => st.user?.role);
  const entity = ENTITY_MODAL_NAMES[groupBy];
  const editable = canEditNotes(entity, role);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const isCellEditable = useCallback((p: GridCellParams) =>
    p.field === 'notes' && UUID.test(String(p.id)) && p.id !== NIL_UUID, []);

  // A keystroke on a selected cell would open the editor emptied, and any click away would save that.
  const onCellEditStart = useCallback((p: GridCellEditStartParams, e: MuiEvent) => {
    if (p.field === 'notes' && p.reason !== 'cellDoubleClick') e.defaultMuiPrevented = true;
  }, []);

  const processRowUpdate = useCallback(async (next: GridValidRowModel, prev: GridValidRowModel) => {
    const notes = String(next.notes ?? '').trim() || null;
    if (notes === (prev.notes ?? null)) return prev;
    await saveNotes(entity, String(prev.id), notes);
    await Promise.all([queryClient.cancelQueries({ queryKey: ['tracking-stats'] }), queryClient.cancelQueries({ queryKey: ['tracking-drilldown'] })]);
    // The rows are rebuilt from the cached report on every render, so the cache must hold the new notes.
    const withNewNotes = (data: any) => (data?.rows ? {
      ...data, rows: data.rows.map((r: any) => (r.group_key === prev.id ? { ...r, notes } : r)),
    } : data);
    queryClient.setQueriesData({ queryKey: ['tracking-stats'] }, withNewNotes);
    queryClient.setQueriesData({ queryKey: ['tracking-drilldown'] }, withNewNotes);
    // Lists and forms elsewhere; the report itself is patched above, so it needn't refetch.
    markNotesStale(queryClient, entity, { report: false });
    return { ...next, notes };
  }, [entity, queryClient]);

  const onProcessRowUpdateError = useCallback((e: any) => {
    enqueueSnackbar(`Couldn't save the notes: ${e?.response?.data?.error ?? e?.message ?? 'unknown error'}`, { variant: 'error' });
  }, [enqueueSnackbar]);

  return useMemo(() => ({ editable, gridProps: editable ? { isCellEditable, onCellEditStart, processRowUpdate, onProcessRowUpdateError, disableClipboardPaste: true } : {} }),
    [editable, isCellEditable, onCellEditStart, processRowUpdate, onProcessRowUpdateError]);
}
